import isEqual from 'lodash/isEqual';
import memoizeOne, { type MemoizedFn } from 'memoize-one';
import { type Change, ChangeSet, simplifyChanges } from 'prosemirror-changeset';
import type { IntlShape } from 'react-intl';

import { isExperimentEnabled } from '@atlaskit/editor-common/deprecated-platform-feature-experiments';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { areNodesEqualIgnoreAttrs } from '@atlaskit/editor-common/utils/document';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction, EditorState } from '@atlaskit/editor-prosemirror/state';
import type { Step as ProseMirrorStep } from '@atlaskit/editor-prosemirror/transform-override';
import { Mapping, type StepMap } from '@atlaskit/editor-prosemirror/transform';
import { type Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type {
	ColorScheme,
	DeletedDiffPlacement,
	DiffDescriptor,
	DiffType,
	InlineDeletedDiffPlacement,
	ShowDiffPlugin,
	SmartDiffThresholds,
} from '../../showDiffPluginType';
import { areDocsEqualByBlockStructureAndText } from '../areDocsEqualByBlockStructureAndText';
import { createDocMarginAnchorWidget } from '../decorations/createAnchorDecorationWidgets';
import { createBlockChangedDecoration } from '../decorations/createBlockChangedDecoration';
import { createInlineChangedDecoration } from '../decorations/createInlineChangedDecoration';
import { createNodeChangedDecorationWidget } from '../decorations/createNodeChangedDecorationWidget';
import {
	createAttributionColorMap,
	type DiffAttributionSpanData,
	getAttributionKey,
	getColorSchemeForChange,
	isAttributionColoringEnabled,
} from '../decorations/colorSchemes/attributions';
import type { ColorScheme as DecorationColorScheme } from '../decorations/colorSchemes/types';
import { extractDiffDescriptors } from '../decorations/decorationKeys';
import {
	type AttrStepContext,
	getAttrChangeRanges,
	stepIsValidAttrChange,
} from '../decorations/utils/getAttrChangeRanges';
import { getMarkChangeRanges } from '../decorations/utils/getMarkChangeRanges';
import { getDefaultDiffType, isExtendedEnabled } from '../isExtendedEnabled';
import type { ShowDiffPluginState } from '../main';
import type { NodeViewSerializer } from '../NodeViewSerializer';

import { attrAwareTokenEncoder } from './attrAwareTokenEncoder';
import { diffBySteps } from './diffBySteps';
import { groupChangesByBlock } from './groupChangesByBlock';
import { optimizeChanges } from './optimizeChanges';
import { simplifyChangesWithAttribution } from './simplifyChangesWithAttribution';
import { simplifySteps, simplifyStepsWithAttribution } from './simplifySteps';
import { classifySmartChanges } from './smart/classifySmartChanges';
import { smartChangeLevel } from './smart/helpers';

type CalculatedDiffs = {
	decorations: DecorationSet;
	diffDescriptors: DiffDescriptor[];
};

const getChanges = ({
	changeset,
	originalDoc,
	steppedDoc,
	diffType,
	tr,
	steps,
	intl,
	smartThresholds,
}: {
	changeset: ChangeSet;
	diffType: DiffType;
	intl: IntlShape;
	originalDoc: PMNode;
	smartThresholds?: Partial<SmartDiffThresholds>;
	steppedDoc: PMNode;
	steps: ProseMirrorStep[];
	tr: Transaction;
}): Change[] => {
	if (isExtendedEnabled(diffType)) {
		// The `smart` diff type is gated behind `platform_editor_ai_smart_diff`. When the gate is
		// off, `smart` falls through to the default (`inline`) path below so behaviour degrades
		// gracefully (see docs/smart-diff-design.md §3).
		if (diffType === 'smart' && fg('platform_editor_ai_smart_diff')) {
			const changes = simplifyChanges(changeset.changes, tr.doc);
			return classifySmartChanges({
				changes,
				originalDoc,
				newDoc: tr.doc,
				locale: intl.locale,
				thresholds: smartThresholds,
			});
		}
		if (diffType === 'step') {
			return diffBySteps(originalDoc, steps);
		}
		if (diffType === 'block') {
			return groupChangesByBlock(changeset.changes, originalDoc, steppedDoc);
		}
		const changes = simplifyChanges(changeset.changes, tr.doc);
		return optimizeChanges(changes);
	}

	const changes = simplifyChanges(changeset.changes, tr.doc);
	return optimizeChanges(changes);
};

const getAttributedChanges = ({
	attributedChanges,
	changeset,
	originalDoc,
	steppedDoc,
	diffType,
	tr,
	intl,
	smartThresholds,
}: {
	attributedChanges: Change[];
	changeset: ChangeSet;
	diffType: DiffType;
	intl: IntlShape;
	originalDoc: PMNode;
	smartThresholds?: Partial<SmartDiffThresholds>;
	steppedDoc: PMNode;
	tr: Transaction;
}): Change[] => {
	if (isExtendedEnabled(diffType)) {
		if (diffType === 'smart' && fg('platform_editor_ai_smart_diff')) {
			return classifySmartChanges({
				changes: simplifyChangesWithAttribution(attributedChanges, tr.doc),
				originalDoc,
				newDoc: tr.doc,
				locale: intl.locale,
				thresholds: smartThresholds,
			});
		}
		if (diffType === 'step') {
			return attributedChanges;
		}
		if (diffType === 'block') {
			return groupChangesByBlock(changeset.changes, originalDoc, steppedDoc);
		}
	}

	return simplifyChangesWithAttribution(attributedChanges, tr.doc);
};

/**
 * Collect the inline-content ranges of every leaf text-bearing block (paragraph, heading, …)
 * whose content overlaps `[from, to)`. Used to clip a node-level `smart` insertion highlight to
 * the actual added text, so the inserted background/underline never spans structural gaps
 * (list markers, empty item slots, cell/column boundaries) which would render as phantom rows.
 */
const leafTextblockRanges = (
	doc: EditorState['doc'],
	from: number,
	to: number,
): Array<[number, number]> => {
	const ranges: Array<[number, number]> = [];
	doc.nodesBetween(from, to, (node, pos) => {
		if (node.isTextblock && node.content.size > 0) {
			const contentFrom = Math.max(pos + 1, from);
			const contentTo = Math.min(pos + 1 + node.content.size, to);
			if (contentTo > contentFrom) {
				ranges.push([contentFrom, contentTo]);
			}
			// Textblocks have no block children to descend into.
			return false;
		}
		return true;
	});
	return ranges;
};

// A large inserted table is treated with a coarse decoration path (see below)
// once it has more leaf textblocks (~cells) than this threshold. Small tables
// keep the precise per-cell path.
const LARGE_TABLE_LEAF_THRESHOLD = 40;

const isLargeInsertedTableRange = (doc: EditorState['doc'], from: number, to: number): boolean => {
	let containsTable = false;
	let leafCount = 0;
	doc.nodesBetween(from, to, (node) => {
		if (node.type.name === 'table') {
			containsTable = true;
		}
		if (node.isTextblock) {
			leafCount += 1;
			return false;
		}
		return true;
	});
	return containsTable && leafCount > LARGE_TABLE_LEAF_THRESHOLD;
};

// Collect the [from, to) ranges of every `table` node overlapping `[from, to)`. The coarse
// large-table path must only suppress decorations inside these ranges
const tableRanges = (
	doc: EditorState['doc'],
	from: number,
	to: number,
): Array<[number, number]> => {
	const ranges: Array<[number, number]> = [];
	doc.nodesBetween(from, to, (node, pos) => {
		if (node.type.name === 'table') {
			ranges.push([pos, pos + node.nodeSize]);
			return false;
		}
		return true;
	});
	return ranges;
};

const isInsideTable = (tables: Array<[number, number]>, pos: number): boolean =>
	tables.some(([tFrom, tTo]) => pos >= tFrom && pos < tTo);

const calculateNodesForBlockDecoration = ({
	doc,
	from,
	to,
	colorScheme,
	isInserted = true,
	activeIndexPos,
	shouldHideDeleted = false,
	showIndicators = false,
	diffType,
	coarseTableCellsOnly = false,
}: {
	activeIndexPos?: { from: number; to: number };
	coarseTableCellsOnly?: boolean;
	colorScheme?: DecorationColorScheme;
	diffType?: DiffType;
	doc: EditorState['doc'];
	from: number;
	intl: IntlShape;
	isInserted?: boolean;
	shouldHideDeleted?: boolean;
	showIndicators?: boolean;
	to: number;
}): Decoration[] => {
	const decorations: Decoration[] = [];
	// Coarse path: inside the large table, only cell/header overlays are kept — the
	// table/row/paragraph decorations there are redundant and cause expensive per-cell
	// re-render. Blocks outside the table keep their normal decorations.
	const coarseTables = coarseTableCellsOnly ? tableRanges(doc, from, to) : [];
	// Iterate over the document nodes within the range
	doc.nodesBetween(from, to, (node, pos) => {
		if (coarseTableCellsOnly && isInsideTable(coarseTables, pos)) {
			const name = node.type.name;
			if (name !== 'tableCell' && name !== 'tableHeader') {
				return;
			}
		}
		if (node.isBlock && (!isExtendedEnabled(diffType) || pos + node.nodeSize <= to)) {
			const nodeEnd = pos + node.nodeSize;
			const isActive =
				activeIndexPos && pos === activeIndexPos.from && nodeEnd === activeIndexPos.to;

			decorations.push(
				...createBlockChangedDecoration({
					change: { from: pos, to: nodeEnd, name: node.type.name },
					colorScheme,
					isInserted,
					isActive,
					shouldHideDeleted,
					showIndicators,
					doc,
					diffType,
				}),
			);
		}
	});

	return decorations;
};

type NodesEqualEvent = {
	action: 'nodesNotEqual';
	actionSubject: 'showDiff';
	attributes: {
		colorScheme: ColorScheme | undefined;
		docSizeEqual: boolean;
		recoveredViaContentEquality?: boolean;
	};
	eventType: 'track';
};

/**
 * Whether deleted content for `change` renders after the new content instead of before it.
 *
 * show-diff splits this across two options by change granularity, so the level decides which one
 * applies:
 * - node/paragraph-level: `deletedDiffPlacement` (default `'top'`). Pure deletions are promoted to
 *   node level, so they are governed here too.
 * - inline/sentence-level: `inlineDeletedDiffPlacement` (default `'before'`).
 *
 * Callers are responsible for the `smart` diffType and gate checks.
 */
export const isDeletedContentPlacedBelow = ({
	change,
	deletedDiffPlacement,
	inlineDeletedDiffPlacement,
}: {
	change: Change;
	deletedDiffPlacement?: DeletedDiffPlacement;
	inlineDeletedDiffPlacement?: InlineDeletedDiffPlacement;
}): boolean => {
	const level = smartChangeLevel(change);

	if (level === 'node' || level === 'paragraph') {
		return deletedDiffPlacement === 'bottom';
	}

	// Inline-level (undefined) and sentence-level changes.
	return inlineDeletedDiffPlacement === 'after';
};

const calculateDiffDecorationsInner = ({
	state,
	pluginState,
	nodeViewSerializer,
	colorScheme,
	intl,
	activeIndexPos,
	api,
	isInverted = false,
	diffType = getDefaultDiffType(),
	hideDeletedDiffs = false,
	hideAddedDiffsUnderline: hideAddedDiffsUnderlineParam = false,
	showIndicators = false,
	smartThresholds,
	deletedDiffPlacement = 'top',
	inlineDeletedDiffPlacement = 'before',
}: {
	activeIndexPos?: { from: number; to: number };
	api: ExtractInjectionAPI<ShowDiffPlugin> | undefined;
	colorScheme?: ColorScheme;
	deletedDiffPlacement?: DeletedDiffPlacement;
	diffType?: DiffType;
	hideAddedDiffsUnderline?: boolean;
	hideDeletedDiffs?: boolean;
	inlineDeletedDiffPlacement?: InlineDeletedDiffPlacement;
	intl: IntlShape;
	isInverted?: boolean;
	nodeViewSerializer: NodeViewSerializer;
	pluginState: Omit<ShowDiffPluginState, 'decorations'>;
	showIndicators?: boolean;
	smartThresholds?: Partial<SmartDiffThresholds>;
	state: EditorState;
}): CalculatedDiffs => {
	const { originalDoc, steps, stepAttributions, isDisplayingChanges } = pluginState;
	if (!originalDoc || !isDisplayingChanges) {
		return { decorations: DecorationSet.empty, diffDescriptors: [] };
	}

	// Resolve the option against its gate once here, so every downstream inline/block builder
	// receives the same value. When the gate is off the option is a no-op.
	const hideAddedDiffsUnderline =
		hideAddedDiffsUnderlineParam && fg('platform_editor_ai_smart_diff');

	const { tr } = state;
	let steppedDoc = originalDoc;
	const attributionColoringEnabled = isAttributionColoringEnabled(stepAttributions);
	let simplifiedSteps: ProseMirrorStep[];
	let simplifiedStepAttributions: NonNullable<ShowDiffPluginState['stepAttributions']> = [];

	if (attributionColoringEnabled) {
		const simplifiedStepsWithAttribution = simplifyStepsWithAttribution(
			steps,
			stepAttributions ?? [],
			originalDoc,
		);
		simplifiedSteps = simplifiedStepsWithAttribution.map(({ step }) => step);
		simplifiedStepAttributions = simplifiedStepsWithAttribution.map(
			({ stepAttribution }) => stepAttribution,
		);
	} else {
		simplifiedSteps = simplifySteps(steps, originalDoc);
	}

	const pendingAttrSteps: Array<
		Omit<AttrStepContext, 'finalPos' | 'originalPos'> & { mapIndex: number }
	> = [];
	const stepMaps: StepMap[] = [];

	for (const step of simplifiedSteps) {
		const result = step.apply(steppedDoc);
		if (result.failed === null && result.doc) {
			if (stepIsValidAttrChange(step, steppedDoc, result.doc)) {
				pendingAttrSteps.push({
					afterNode: result.doc.nodeAt(step.pos),
					beforeNode: steppedDoc.nodeAt(step.pos),
					mapIndex: stepMaps.length,
					step,
				});
			}
			stepMaps.push(step.getMap());
			steppedDoc = result.doc;
		}
	}
	// Gate the complete attribution fix: mapped positions and step-time nodes are used together.
	// The fallback deliberately preserves the legacy raw-position lookups for rollout safety.
	const attrStepContexts = fg('platform_editor_reduce_diff_attr_sensitivity')
		? pendingAttrSteps.flatMap<AttrStepContext>(({ afterNode, beforeNode, mapIndex, step }) => {
				const finalPosition = new Mapping(stepMaps.slice(mapIndex + 1)).mapResult(step.pos);
				if (finalPosition.deleted) {
					return [];
				}

				const originalPosition = new Mapping(stepMaps.slice(0, mapIndex))
					.invert()
					.mapResult(step.pos);

				return [
					{
						afterNode,
						beforeNode,
						finalPos: finalPosition.pos,
						...(originalPosition.deleted ? {} : { originalPos: originalPosition.pos }),
						step,
					},
				];
			})
		: pendingAttrSteps.map(({ step }) => ({
				afterNode: tr.doc.nodeAt(step.pos),
				beforeNode: originalDoc.nodeAt(step.pos),
				finalPos: step.pos,
				originalPos: step.pos,
				step,
			}));

	// Rather than using .eq() we use a custom function that only checks for structural
	// changes and ignores differences in attributes which don't affect decoration positions
	if (!areNodesEqualIgnoreAttrs(steppedDoc, tr.doc)) {
		const recoveredViaContentEquality = areDocsEqualByBlockStructureAndText(steppedDoc, tr.doc);

		api?.analytics?.actions.fireAnalyticsEvent<NodesEqualEvent, 'customEventType'>({
			eventType: 'track',
			action: 'nodesNotEqual',
			actionSubject: 'showDiff',
			attributes: {
				docSizeEqual: steppedDoc.nodeSize === tr.doc.nodeSize,
				colorScheme,
				recoveredViaContentEquality,
			},
		});

		if (!recoveredViaContentEquality) {
			return { decorations: DecorationSet.empty, diffDescriptors: [] };
		}
	}
	// The attribute-aware encoder is only needed by the smart classifier and is
	// gated with it; other diff types keep the library default so their output is
	// unchanged.
	const tokenEncoder =
		diffType === 'smart' && fg('platform_editor_ai_smart_diff') ? attrAwareTokenEncoder : undefined;
	let changes: Change[];
	let attributedChanges: Change[] = [];
	let attributionColors: ReturnType<typeof createAttributionColorMap> | undefined;

	if (attributionColoringEnabled) {
		const changeset = ChangeSet.create<DiffAttributionSpanData>(
			originalDoc,
			(left, right) =>
				getAttributionKey(left) === getAttributionKey(right)
					? left.stepIndex > right.stepIndex
						? left
						: right
					: // `prosemirror-changeset` uses null as its incompatible-data sentinel even
						// though its public combine callback is typed as returning Data.
						(null as unknown as DiffAttributionSpanData),
			tokenEncoder,
		).addSteps(
			steppedDoc,
			stepMaps,
			simplifiedStepAttributions.map((attribution, stepIndex) => ({
				...attribution,
				stepIndex,
			})),
		);
		attributedChanges = diffBySteps(originalDoc, simplifiedSteps, simplifiedStepAttributions);
		changes = getAttributedChanges({
			changeset,
			originalDoc,
			steppedDoc,
			diffType,
			tr,
			attributedChanges,
			intl,
			smartThresholds,
		});
		attributionColors = createAttributionColorMap(simplifiedStepAttributions);
	} else {
		const changeset = ChangeSet.create(originalDoc, undefined, tokenEncoder).addSteps(
			steppedDoc,
			stepMaps,
			tr.doc,
		);
		changes = getChanges({
			changeset,
			originalDoc,
			steppedDoc,
			diffType,
			tr,
			steps: simplifiedSteps,
			intl,
			smartThresholds,
		});
	}

	const decorations: Decoration[] = [];

	/**
	 * If showIndicators is on, we create an anchor widget here to mark the doc margin.
	 */
	if (showIndicators && isExtendedEnabled(diffType)) {
		decorations.push(createDocMarginAnchorWidget());
	}

	// Our default operations are insertions, so it should match the opposite of isInverted.
	const isInserted = !isInverted;

	const createDecorationsForChange = (change: Change): void => {
		const changeColorScheme = attributionColors
			? getColorSchemeForChange(change, attributedChanges, attributionColors, colorScheme)
			: colorScheme;
		const isActive =
			activeIndexPos && change.fromB === activeIndexPos.from && change.toB === activeIndexPos.to;

		// Hoisted because it decides BOTH where the deleted widget is anchored and — since the
		// widget pins whichever end of the range it sits at — how the indicator anchors below are
		// allowed to move.
		const isDeletedWidgetBelow =
			diffType === 'smart' &&
			fg('platform_editor_ai_smart_diff') &&
			isDeletedContentPlacedBelow({ change, deletedDiffPlacement, inlineDeletedDiffPlacement });

		if (change.inserted.length > 0) {
			// On an inverted diff the inserted side is visually the deleted side.
			const shouldHideDeleted = isExtendedEnabled(diffType)
				? isInverted && hideDeletedDiffs
				: false;

			// For `smart` NODE-level promotions the change range spans a whole container
			// (e.g. an entire list/table/layout, using outer node bounds). Applying a SINGLE
			// inline decoration across that whole range would paint the inserted style across
			// block boundaries and structural gaps (list markers, empty item slots), producing
			// phantom "empty" rows above the real content. But skipping the inline highlight
			// entirely leaves added text-bearing blocks (paragraphs/headings inside the added
			// container) without the inserted background+underline, because block decorations
			// return no style for paragraph/heading. So for node-level smart changes we instead
			// emit ONE inline decoration per leaf text-bearing block within the range — the text
			// gets highlighted, and structural gaps never do.
			const isSmartNodeLevel =
				diffType === 'smart' &&
				fg('platform_editor_ai_smart_diff') &&
				smartChangeLevel(change) === 'node';
			// For a large inserted table, skip the per-cell inline decorations that
			// freeze the browser on re-render. Only active in the AIFC experience.
			const useCoarseTableDecoration =
				isSmartNodeLevel &&
				expValEquals(
					'platform_editor_ai_new_aifc_editor_experience',
					'isBackendReviewMomentEnabled',
					true,
				) &&
				isLargeInsertedTableRange(tr.doc, change.fromB, change.toB);
			// Whether the deleted-content widget will actually be rendered for this
			// change. Used to decide if indicator anchor positions should be adjusted
			// inward — when the widget is present the anchor must stay at the block
			// boundary to keep the indicator bar continuous with the deleted content.
			const willRenderDeletedWidget =
				change.deleted.length > 0 &&
				!(
					isExtendedEnabled(diffType) &&
					!isInverted &&
					hideDeletedDiffs &&
					change.inserted.length > 0
				);

			if (isSmartNodeLevel) {
				// For a large inserted table, skip the O(cells) inline decorations inside the
				// table (the cell overlays added below provide the highlight without the
				// expensive content re-render); leaf blocks outside the table still get theirs.
				const coarseTables = useCoarseTableDecoration
					? tableRanges(tr.doc, change.fromB, change.toB)
					: [];
				for (const [from, to] of leafTextblockRanges(tr.doc, change.fromB, change.toB)) {
					if (useCoarseTableDecoration && isInsideTable(coarseTables, from)) {
						continue;
					}
					decorations.push(
						...createInlineChangedDecoration({
							change: { fromB: from, toB: to },
							doc: tr.doc,
							colorScheme: changeColorScheme,
							isActive,
							diffType,
							...(isExtendedEnabled(diffType) && {
								isInserted,
								shouldHideDeleted,
								showIndicators,
								hideAddedDiffsUnderline,
								hasDeletedWidget: willRenderDeletedWidget,
								isDeletedWidgetBelow,
							}),
						}),
					);
				}
			} else {
				decorations.push(
					...createInlineChangedDecoration({
						change,
						doc: tr.doc,
						colorScheme: changeColorScheme,
						isActive,
						diffType,
						...(isExtendedEnabled(diffType) && {
							isInserted,
							shouldHideDeleted,
							showIndicators,
							hideAddedDiffsUnderline,
							hasDeletedWidget: willRenderDeletedWidget,
							isDeletedWidgetBelow,
						}),
					}),
				);
			}

			decorations.push(
				...calculateNodesForBlockDecoration({
					doc: tr.doc,
					from: change.fromB,
					to: change.toB,
					colorScheme: changeColorScheme,
					...(isExtendedEnabled(diffType) && {
						isInserted,
						shouldHideDeleted,
						showIndicators,
					}),
					activeIndexPos,
					intl,
					diffType,
					coarseTableCellsOnly: useCoarseTableDecoration,
				}),
			);
		}
		if (change.deleted.length > 0) {
			const shouldHideDeleted = isExtendedEnabled(diffType)
				? !isInverted && hideDeletedDiffs && change.inserted.length > 0
				: false;
			if (!shouldHideDeleted) {
				decorations.push(
					...createNodeChangedDecorationWidget({
						change,
						doc: originalDoc,
						nodeViewSerializer,
						colorScheme: changeColorScheme,
						newDoc: tr.doc,
						intl,
						activeIndexPos,
						...(isExtendedEnabled(diffType) && {
							isInserted: !isInserted,
							diffType,
							hideAddedDiffsUnderline,
							placeBelow: isDeletedWidgetBelow,
						}),
						showIndicators,
					}),
				);
			}
		}
	};

	changes.forEach((change) => {
		createDecorationsForChange(change);
	});
	getMarkChangeRanges(steps).forEach((change) => {
		const isActive =
			activeIndexPos && change.fromB === activeIndexPos.from && change.toB === activeIndexPos.to;
		decorations.push(
			...createInlineChangedDecoration({
				change,
				colorScheme,
				isActive,
				isInserted: true,
			}),
		);
	});
	getAttrChangeRanges(tr.doc, attrStepContexts, originalDoc).forEach((change) => {
		if (change.isInline) {
			// Inline nodes (e.g. date, emoji, mention, status) need an inline decoration rather than a block decoration
			const isActive =
				activeIndexPos && change.fromB === activeIndexPos.from && change.toB === activeIndexPos.to;
			const isAtomicInlineNodeFlag = isExperimentEnabled(
				'platform_editor_improve_inline_diffs',
				() => expValEquals('platform_editor_improve_inline_diffs', 'isEnabled', true),
			);
			decorations.push(
				...createInlineChangedDecoration({
					change,
					colorScheme,
					isActive,
					isInserted: true,
					isAtomicInlineNode: isAtomicInlineNodeFlag,
					inlineNodeName: change.inlineNodeName,
					// Suppress the border-bottom underline for atomic inline nodeviews —
					// it doesn't render on custom nodeview DOM elements and is unnecessary noise.
					hideAddedDiffsUnderline: isAtomicInlineNodeFlag,
					diffType,
				}),
			);
			// If we have the original node position, also render the old node as a "deleted" widget
			// so the user can see what it looked like before the change.
			if (
				change.fromA !== undefined &&
				change.toA !== undefined &&
				isExperimentEnabled('platform_editor_improve_inline_diffs', () =>
					expValEquals('platform_editor_improve_inline_diffs', 'isEnabled', true),
				)
			) {
				decorations.push(
					...createNodeChangedDecorationWidget({
						change: {
							fromA: change.fromA,
							toA: change.toA,
							fromB: change.fromB,
							toB: change.toB,
							deleted: [],
						},
						doc: originalDoc,
						nodeViewSerializer,
						colorScheme,
						newDoc: tr.doc,
						intl,
						activeIndexPos,
						isInserted: false,
					}),
				);
			}
		} else {
			decorations.push(
				...calculateNodesForBlockDecoration({
					doc: tr.doc,
					from: change.fromB,
					to: change.toB,
					colorScheme,
					isInserted: true,
					activeIndexPos,
					intl,
					showIndicators,
				}),
			);
			// If the original node position is known (e.g. a panel type change), also render
			// the old block node as a "deleted" widget so the reviewer sees the before/after.
			const isSmartNodeLevelAttrChange =
				diffType === 'smart' &&
				fg('platform_editor_ai_smart_diff') &&
				expValEquals('platform_editor_ai_new_aifc_editor_experience', 'isEnabled', true);
			if (isSmartNodeLevelAttrChange && change.fromA !== undefined && change.toA !== undefined) {
				const placeBelow = deletedDiffPlacement === 'bottom';
				decorations.push(
					...createNodeChangedDecorationWidget({
						change: {
							fromA: change.fromA,
							toA: change.toA,
							fromB: change.fromB,
							toB: change.toB,
							deleted: [],
						},
						doc: originalDoc,
						nodeViewSerializer,
						colorScheme,
						newDoc: tr.doc,
						intl,
						activeIndexPos,
						isInserted: false,
						...(isExtendedEnabled(diffType) && {
							diffType,
							hideAddedDiffsUnderline,
							placeBelow,
						}),
						showIndicators,
					}),
				);
			}
		}
	});

	const decorationSet = DecorationSet.empty.add(tr.doc, decorations);

	return {
		decorations: decorationSet,
		diffDescriptors: extractDiffDescriptors(decorationSet),
	};
};

export const calculateDiffDecorations: MemoizedFn<
	({
		state,
		pluginState,
		nodeViewSerializer,
		colorScheme,
		intl,
		activeIndexPos,
		api,
		hideDeletedDiffs,
		hideAddedDiffsUnderline,
		showIndicators,
	}: {
		activeIndexPos?: {
			from: number;
			to: number;
		};
		api: ExtractInjectionAPI<ShowDiffPlugin> | undefined;
		colorScheme?: ColorScheme;
		deletedDiffPlacement?: DeletedDiffPlacement;
		hideAddedDiffsUnderline?: boolean;
		hideDeletedDiffs?: boolean;
		inlineDeletedDiffPlacement?: InlineDeletedDiffPlacement;
		intl: IntlShape;
		nodeViewSerializer: NodeViewSerializer;
		pluginState: Omit<ShowDiffPluginState, 'decorations'>;
		showIndicators?: boolean;
		smartThresholds?: Partial<SmartDiffThresholds>;
		state: EditorState;
	}) => CalculatedDiffs
> = memoizeOne(
	calculateDiffDecorationsInner,
	// Cache results unless relevant inputs change
	(
		[
			{
				pluginState,
				state,
				colorScheme,
				intl,
				activeIndexPos,
				isInverted,
				diffType,
				hideDeletedDiffs,
				hideAddedDiffsUnderline,
				showIndicators,
				smartThresholds,
				deletedDiffPlacement,
				inlineDeletedDiffPlacement,
			},
		],
		[
			{
				pluginState: lastPluginState,
				state: lastState,
				colorScheme: lastColorScheme,
				intl: lastIntl,
				activeIndexPos: lastActiveIndexPos,
				isInverted: lastIsInverted,
				diffType: lastDiffType,
				hideDeletedDiffs: lastHideDeletedDiffs,
				hideAddedDiffsUnderline: lastHideAddedDiffsUnderline,
				showIndicators: lastShowIndicators,
				smartThresholds: lastSmartThresholds,
				deletedDiffPlacement: lastDeletedDiffPlacement,
				inlineDeletedDiffPlacement: lastInlineDeletedDiffPlacement,
			},
		],
	) => {
		const originalDocIsSame =
			lastPluginState.originalDoc &&
			pluginState.originalDoc &&
			pluginState.originalDoc.eq(lastPluginState.originalDoc);

		if (isExtendedEnabled(diffType)) {
			return (
				(colorScheme === lastColorScheme &&
					intl.locale === lastIntl.locale &&
					isInverted === lastIsInverted &&
					diffType === lastDiffType &&
					isEqual(activeIndexPos, lastActiveIndexPos) &&
					originalDocIsSame &&
					isEqual(pluginState.steps, lastPluginState.steps) &&
					isEqual(pluginState.stepAttributions, lastPluginState.stepAttributions) &&
					state.doc.eq(lastState.doc) &&
					hideDeletedDiffs === lastHideDeletedDiffs &&
					hideAddedDiffsUnderline === lastHideAddedDiffsUnderline &&
					showIndicators === lastShowIndicators &&
					isEqual(smartThresholds, lastSmartThresholds) &&
					deletedDiffPlacement === lastDeletedDiffPlacement &&
					inlineDeletedDiffPlacement === lastInlineDeletedDiffPlacement) ??
				false
			);
		}
		return (
			(originalDocIsSame &&
				isEqual(pluginState.steps, lastPluginState.steps) &&
				isEqual(pluginState.stepAttributions, lastPluginState.stepAttributions) &&
				state.doc.eq(lastState.doc) &&
				colorScheme === lastColorScheme &&
				intl.locale === lastIntl.locale &&
				isEqual(activeIndexPos, lastActiveIndexPos) &&
				hideDeletedDiffs === lastHideDeletedDiffs) ??
			false
		);
	},
);
